import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import {
	Branch,
	ContactInfo,
	FinancialInstitutionLender,
	MortgageeType,
	PopularCSLenders,
	PopularLenders,
} from '@models/lender';
import { MatterFileType } from '@models/matter';
import { Select } from '@ngxs/store';
import { InstitutionRepo } from '@repos/institution/institution.service';
import { InstitutionBranchRepo } from '@repos/institutionBranch/institution-branch.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Action } from '@state/utils/models';
import { cloneDeep, isEqual } from 'lodash';
import LogRocket from 'logrocket';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import { SnackbarService } from '@services/ui/snackbar.service';
import { checkIfMajorLender } from '@core/util/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { onSelectInstitution } from '../../searchable-fields/lender-search-input/lender-search-input.types';
import {
	CreateInstitutionComponent,
	CreateInstitutionData,
} from 'src/app/private/sale/encumbrances/encumbrance/components/institution/create-institution/create-institution.component';
import {
	CreateInstitutionBranchComponent,
	CreateOrUpdateInstitutionModel,
} from 'src/app/private/sale/encumbrances/encumbrance/components/institution/create-branch/create-branch.component';
import { OnSelectBranch } from '@components/searchable-fields/branch-search/branch-search.component';

export enum dropdownType {
	EDIT = 'Edit',
	REMOVE = 'Remove',
	ADD_BRANCH = 'Add Branch',
}

const dropDownItems: DropdownOption<dropdownType>[] = [
	{
		name: 'Edit',
		icon: ICON.EDIT,
		value: dropdownType.EDIT,
	},
	{
		name: 'Add branch',
		icon: ICON.ADD,
		value: dropdownType.ADD_BRANCH,
	},
	{
		name: 'Remove',
		icon: ICON.DELETE,
		value: dropdownType.REMOVE,
	},
];

@UntilDestroy()
@Component({
	selector: 'app-encumbrance-institution-card',
	templateUrl: './encumbrance-institution-card.component.html',
	styleUrls: ['./encumbrance-institution-card.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class EncumbranceInstitutionCardComponent implements OnInit {
	public financialInstitution: FinancialInstitutionLender;
	public allowInstEdit = true;

	@Input() mortgageeType: MortgageeType;
	@Input() set finInstitution(institution: FinancialInstitutionLender) {
		this.financialInstitution = cloneDeep(institution);
		this.setContactDetails(institution);
		this.setBranchContactDetails(institution?.branch);
		if (institution) {
			this.branchDropDownItems = dropDownItems.filter(
				(item) => item.value !== dropdownType.ADD_BRANCH,
			);
			if (this.showDischargeFields) {
				this.allowInstEdit =
					!['001', '002', '003'].includes(this.financialInstitution.id) &&
					!(
						this.mortgageeType === MortgageeType.COMPUTERSHARE &&
						this.financialInstitution?.branch?.id === 'HPtJMx4'
					);
				if (!this.allowInstEdit) {
					this.institutionDropDownItems = dropDownItems.filter(
						(item) => item.value !== dropdownType.EDIT,
					);
				} else {
					this.institutionDropDownItems = dropDownItems;
				}
			} else {
				this.institutionDropDownItems = this.branchDropDownItems;
			}
		}
	}

	@Input() showDischargeFields: boolean;
	@Input() pushChangesToGlobalData = true;

	@Select(SelectedMatter.matterFileType) fileType$: Observable<MatterFileType>;

	@Output() editBranch: EventEmitter<Branch> = new EventEmitter();
	@Output() removeBranch: EventEmitter<void> = new EventEmitter();
	@Output() setBranch: EventEmitter<Branch> = new EventEmitter();
	@Output() createBranch: EventEmitter<Branch> = new EventEmitter();

	@Output() editInstitution: EventEmitter<FinancialInstitutionLender> =
		new EventEmitter();
	@Output() selectInstitution: EventEmitter<FinancialInstitutionLender> =
		new EventEmitter();
	@Output() removeInstitution: EventEmitter<FinancialInstitutionLender> =
		new EventEmitter();
	@Output() createInstitution: EventEmitter<FinancialInstitutionLender> =
		new EventEmitter();

	public ICON = ICON;
	public branchDropDownItems = dropDownItems;
	public institutionDropDownItems = dropDownItems;
	public mortgageeTypes = MortgageeType;
	public lenders = PopularLenders;
	public popularCSLenders = PopularCSLenders;
	public fileTypes = MatterFileType;
	public fileType: MatterFileType;
	public institutionContactDetails: ContactInfo;
	public escalationContactDetails: ContactInfo;
	public branchContactDetails: ContactInfo;
	public branchEscalationContactDetails: ContactInfo;
	public showBranchEscalation = false;
	public showInstitutionEscalation = false;
	public showInstitutionPrimary = false;
	public showAddBranch = false;
	public showBranchPayoutDetails = false;

	constructor(
		private dialog: MatDialog,
		private institutionRepo: InstitutionRepo,
		private branchRepo: InstitutionBranchRepo,
		private snackbar: SnackbarService,
	) {}

	ngOnInit(): void {
		this.fileType$.pipe(take(1), untilDestroyed(this)).subscribe((fileType) => {
			this.fileType = fileType;
			this.setContactDetails(this.financialInstitution);
			this.setBranchContactDetails(this.financialInstitution?.branch);
		});
	}

	_editBranch(): void {
		const data: CreateOrUpdateInstitutionModel = {
			showDischargeFields: this.showDischargeFields,
			branch: this.financialInstitution?.branch,
			institutionId: this.financialInstitution?.id,
			showTransitNumber: this.mortgageeType === MortgageeType.INSTITUTION,
			branchLabel:
				this.mortgageeType === MortgageeType.COMPUTERSHARE
					? 'Lender'
					: 'Branch',
			institutionBadgeDetails:
				this.mortgageeType === MortgageeType.COMPUTERSHARE
					? {
							name: this.financialInstitution?.branch?.name,
							logoUrl: this.financialInstitution.branch.logoUrl,
						}
					: cloneDeep(this.financialInstitution),
			fileType: this.fileType,
			mortgageeType: this.mortgageeType,
			create: false,
		};

		const _dialog = this.dialog.open(CreateInstitutionBranchComponent, {
			data,
			width: '80vw',
			maxWidth: '800px',
		});

		_dialog
			.afterClosed()
			.pipe(take(1))
			.subscribe((result: Branch) => {
				if (result) {
					if (!isEqual(this.financialInstitution?.branch, result)) {
						this.editBranch.emit({
							...this.financialInstitution?.branch,
							...result,
						});
						this.setBranchContactDetails(result);
						this.toggleSnackbar();
					}
				}
			});
	}

	async setLeadingBank(institutionId: string): Promise<void> {
		try {
			const institution = await this.institutionRepo
				.Get(institutionId)
				.pipe(take(1))
				.toPromise();
			const financialInstitution = {
				...institution.data(),
				id: institution.id,
			} as FinancialInstitutionLender;
			this.selectInstitution.emit(financialInstitution);
			this.setContactDetails(financialInstitution);
		} catch (err) {
			LogRocket.error('Error fetching institution with id', institutionId, err);
		}
	}

	dropdownOptionSelect(ev: DropdownResult<DropdownOption<dropdownType>>): void {
		if (ev.result.value === dropdownType.REMOVE) {
			this.removeInstitution.emit();
		} else if (ev.result.value === dropdownType.EDIT) {
			this.updateInstitution();
		} else if (ev.result.value === dropdownType.ADD_BRANCH) {
			this.showAddBranch = true;
		}
	}

	branchDropdownOptionSelect(
		ev: DropdownResult<DropdownOption<dropdownType>>,
	): void {
		if (ev.result.value === dropdownType.REMOVE) {
			this._removeBranch();
		} else if (ev.result.value === dropdownType.EDIT) {
			this._editBranch();
		}
	}

	toggleAddBranch(): void {
		this.showAddBranch = !this.showAddBranch;
	}

	onInstitutionSelect(ev: onSelectInstitution): void {
		this.setContactDetails(this.financialInstitution);
		this.branchContactDetails = null;
		this.branchEscalationContactDetails = null;
		this.showBranchEscalation = false;

		if (ev.action === Action.SET)
			this.selectInstitution.emit(ev.financialInstitution);

		if (ev.action === Action.CREATE)
			this.createInstitution.emit(ev.financialInstitution);
	}

	removeFinancialInstitution(): void {
		this.removeInstitution.emit();
	}

	updateInstitution(): void {
		const institutionModel: CreateInstitutionData = {
			showDischargeFields: this.showDischargeFields,
			fileType: this.fileType,
			institution: this.financialInstitution,
			create: false,
			majorBank: checkIfMajorLender(
				this.financialInstitution?.id || '',
				this.mortgageeType,
			),
			type: this.mortgageeType,
			pushChangesToGlobalData: this.pushChangesToGlobalData,
		};
		const dialogRef = this.dialog.open(CreateInstitutionComponent, {
			data: institutionModel,
			width: '80vw',
			maxWidth: '800px',
		});

		dialogRef
			.afterClosed()
			.pipe(take(1), untilDestroyed(this))
			.subscribe((result: FinancialInstitutionLender) => {
				if (result) {
					if (!isEqual(this.financialInstitution, result)) {
						this.editInstitution.emit({
							...this.financialInstitution,
							...result,
						});
						this.setContactDetails(result);
						this.toggleSnackbar();
					}
				}
			});
	}

	async setLeadingCSLender(branchId: string): Promise<void> {
		try {
			const branchResponse = await this.branchRepo
				.Get(branchId, { financialInstitutionId: this.financialInstitution.id })
				.pipe(take(1))
				.toPromise();

			const branchData = branchResponse.data();

			const branch = {
				...branchData,
				id: branchData.id,
				logoUrl: branchData?.logoUrl ?? null,
			} as Branch;

			this.setBranch.emit(branch);
			this.setBranchContactDetails(branch);
		} catch (err) {
			console.log('ERROR: ', err);
			LogRocket.error('Error fetching institution with id', branchId, err);
		}
	}

	onSelectedBranch(selectedBranch: OnSelectBranch): void {
		this.setBranchContactDetails(selectedBranch.branch);

		if (selectedBranch.action === Action.SET)
			this.setBranch.emit(selectedBranch.branch);

		if (selectedBranch.action === Action.CREATE)
			this.createBranch.emit(selectedBranch.branch);
	}

	switchBranch(): void {
		this.showAddBranch = true;
		this._removeBranch();
	}

	_removeBranch(): void {
		this.removeBranch.emit();
	}

	setContactDetails(financialInstitution: FinancialInstitutionLender): void {
		if (financialInstitution) {
			const contactInfo: ContactInfo = {
				phone: financialInstitution.phone,
				fax: financialInstitution.fax,
				email: financialInstitution.email,
				phone2: financialInstitution.phone2,
				email2: financialInstitution.email2,
			};
			if (this.showDischargeFields) {
				this.institutionContactDetails =
					financialInstitution?.discharge?.primary || {};
				this.escalationContactDetails =
					financialInstitution?.discharge?.escalation || {};
				if (Object.values(this.escalationContactDetails).some((x) => x)) {
					this.showInstitutionEscalation = true;
				} else {
					this.showInstitutionEscalation = false;
				}

				if (Object.values(this.institutionContactDetails).some((x) => x)) {
					this.showInstitutionPrimary = true;
				} else {
					this.showInstitutionPrimary = false;
				}
			} else {
				this.institutionContactDetails = contactInfo;
			}
		}
	}

	setBranchContactDetails(branch: Branch): void {
		if (branch) {
			const contactInfo: ContactInfo = {
				phone: branch.phone,
				fax: branch.fax,
				email: branch.email,
				phone2: branch.phone2,
				email2: branch.email2,
			};
			if (this.showDischargeFields) {
				this.branchContactDetails = branch.discharge?.primary || {};
				this.branchEscalationContactDetails =
					branch.discharge?.escalation || {};
				if (
					Object.values(this.branchEscalationContactDetails || {}).some(
						(value) => !!value,
					)
				) {
					this.showBranchEscalation = true;
				} else {
					this.showBranchEscalation = false;
				}
				if (branch.id !== 'HPtJMx4') {
					this.showBranchPayoutDetails = true;
				} else {
					this.showBranchPayoutDetails = false;
				}
			} else {
				this.branchContactDetails = contactInfo;
			}
		}
	}

	toggleSnackbar(): void {
		if (this.pushChangesToGlobalData) {
			this.snackbar.openSnackBar({
				text: 'Lender details submitted for review.',
				type: 'success',
			});
		}
	}
}
